<script lang="ts" setup>
import 'vue-toast-notification/dist/theme-sugar.css';
import { Meta, useHead, UseHeadInput } from '@unhead/vue';
import { useSSRContext, watch } from 'vue';
import { useRoute } from 'vue-router';

import { pushLogError } from '@/api/log';
import LoadingIndicatorBar from '@/components/LoadingIndicatorBar.vue';
import ClientOnly from '@/core/client-only';
import { ComponentError } from '@/core/component-error';
import {
  NotFoundError,
  PermissionDeniedError,
  UnauthenticatedError,
} from '@/core/errors';
import ModalRoot from '@/core/ModalGroup.vue';
import { isRouteLoading } from '@/core/routing';
import headers from '@/headers';
import ErrorLayout from '@/layouts/Error.vue';
import NotFound from '@/layouts/NotFound.vue';

const props = defineProps<{
  meta?: Meta[];
}>();

const headInput: UseHeadInput = {
  htmlAttrs: {},
  ...headers,
  link: [...headers.link],
  meta: [
    { charset: 'UTF-8' },
    { name: 'viewport', content: 'width=device-width, initial-scale=1.0' },
    ...headers.meta,
    ...(props.meta ?? []),
  ],
};

const currentRoute = useRoute();

if (import.meta.env.SSR) {
  const context = useSSRContext();
  if (context) {
    watch(
      () => !!currentRoute.meta.noHydrate,
      (noHydrate) => {
        context.noHydrate = noHydrate;
      },
      { immediate: true },
    );
  }

  headInput.htmlAttrs = { ...headInput.htmlAttrs, lang: context?.locale };
}

useHead(headInput);

if (!import.meta.env.SSR) {
  watch(
    () => currentRoute.meta.error,
    (error) => {
      if (!error) return;

      const err = error instanceof ComponentError ? error.err : error;

      if (
        err instanceof UnauthenticatedError ||
        err instanceof PermissionDeniedError ||
        err instanceof NotFoundError
      ) {
        console.warn(err, err.details);

        return;
      }

      pushLogError('error', error);
    },
    { immediate: true },
  );
}
</script>

<template>
  <NotFound
    v-if="$route.meta.error instanceof NotFoundError"
    :error="$route.meta.error.message"
  />

  <ErrorLayout v-else-if="$route.meta.error" :error="$route.meta.error" />

  <template v-else>
    <RouterView v-slot="{ Component, route }">
      <ClientOnly>
        <LoadingIndicatorBar v-if="isRouteLoading"></LoadingIndicatorBar>

        <template #placeholder>
          <LoadingIndicatorBar
            v-if="!route.meta.noHydrate"
          ></LoadingIndicatorBar>
        </template>
      </ClientOnly>

      <component
        :is="route.meta.layout"
        v-if="route.meta.layout"
        v-bind="route.meta.layoutProps"
        ><component :is="Component" :key="route.params.pageKey ?? route.path"
      /></component>
      <component
        :is="Component"
        v-else
        :key="route.params.pageKey ?? route.path"
      />
    </RouterView>

    <ModalRoot />
  </template>
</template>
